import React, { useState } from 'react';

type SectionConfig = {
  id: string;
  title: string;
  content: React.ReactNode;
};

const sections: SectionConfig[] = [
  {
    id: 'jornadas',
    title: 'Jornadas',
    content: (
      <div className="space-y-3 text-white/70 leading-relaxed">
        <p>
          En Fantasy LUB cada jornada corresponde a una fecha oficial de la Liga Uruguaya de Basketball. La temporada
          regular se disputa a dos ruedas (local y visitante): son 22 fechas en total, con 12 equipos y 6 partidos por
          jornada.
        </p>
        <p>
          Cuando termina la fase regular, la tabla se divide en dos grupos: los 6 mejores disputan la liguilla y los 6
          restantes juegan la reclasificación. Cada grupo suma 10 fechas extra, también con 6 partidos en cada jornada.
        </p>
        <p>
          Después llegan los playoffs: cuartos de final y semifinales se juegan al mejor de 5 partidos, mientras que la
          final se define al mejor de 7. En Fantasy tratamos cada fecha de esos cruces como una jornada independiente.
        </p>
        <p>
          El juego se vive fecha a fecha: al cierre de cada jornada se suman los puntos que tu plantel obtiene con el
          rendimiento real de sus jugadores, y esa puntuación se acumula en la clasificación general.
        </p>
      </div>
    ),
  },
  {
    id: 'construccion-equipo',
    title: 'Construyendo tu equipo',
    content: (
      <div className="space-y-4 text-white/70 leading-relaxed">
        <div className="space-y-2">
          <h3 className="text-base font-bold text-white">Elegí el nombre de tu equipo</h3>
          <p>
            Cuando creás tu plantel vas a elegir un nombre.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-base font-bold text-white">Plantilla de 10 jugadores</h3>
          <p>Tu equipo se arma con 10 jugadores y está dividido por posiciones y roles:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>5 perímetrales</li>
            <li>5 interiores</li>
          </ul>
          <p>
            De esos diez, cinco serán titulares (3 perímetrales y 2 interiores) y cinco quedarán como suplentes (3
            perímetrales y 2 interiores). Podés moverlos cuando los cambios estén habilitados.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-base font-bold text-white">Categorías obligatorias</h3>
          <p>
            Cada jugador del Fantasy LUB mantiene la categoría que tiene en la liga real: extranjero, ficha nacional o
            sub 23. Tu plantilla debe respetar siempre esta combinación:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>3 extranjeros</li>
            <li>5 fichas nacionales</li>
            <li>2 jugadores sub 23</li>
          </ul>
          <p>Estos cupos se pueden repartir libremente entre titulares y suplentes mientras mantengas el total.</p>
        </div>

        <div className="space-y-2">
          <h3 className="text-base font-bold text-white">Presupuesto virtual</h3>
          <p>
            Disponés de un presupuesto de 100 millones para comprar a tus 10 jugadores. Cada jugador tiene un precio
            asignado y vas a tener que balancear tus fichajes para no superar ese límite.
          </p>
        </div>

        <div className="space-y-2">
          <h3 className="text-base font-bold text-white">Empieza más tarde</h3>
          <p>Puedes crear un equipo en cualquier momento durante la competición.</p>
          <p>
            Si creas un equipo entre jornadas, comenzarás a sumar puntos a partir de la siguiente jornada.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'gestionar-equipo',
    title: 'Gestionar el equipo',
    content: (
      <div className="space-y-4 text-white/70 leading-relaxed">
        <p>
          Elegí tu quinteto titular y la banca antes de cada jornada: vas a definir 5 titulares (3 perímetrales y 2
          interiores) y 5 suplentes que completan tu plantel de diez.
        </p>
        <p>
          La suma de puntos en Fantasy LUB se hace con los diez jugadores que lleves a la fecha: tanto titulares como
          suplentes acumulan según su rendimiento real, siempre que se mantengan en tu equipo cuando arranca la jornada.
        </p>
        <p>
          Durante la jornada no hay sustituciones; cualquier modificación que hagas se aplica recién en la siguiente
          fecha. Por eso, asegurate de dejar el equipo listo antes de la hora límite, fijada en 15 minutos previos al
          primer partido.
        </p>
        <p>
          Designá un capitán entre tus cinco titulares. Ese jugador recibe un multiplicador de ×1.5 sobre los puntos que
          obtenga en esa jornada. Si finalmente no juega, no vas a poder cambiar de capitán hasta la próxima fecha.
        </p>
      </div>
    ),
  },
  {
    id: 'transferencias',
    title: 'Transferencias',
    content: (
      <div className="space-y-3 text-white/70 leading-relaxed">
        <p>
          Podés hacer hasta 2 transferencias por fecha para ajustar tu plantel. Cada vez que vendas a un jugador y
          compres otro, el reemplazo tiene que ser de la misma posición (perímetral o interior) y de la misma categoría
          contractual (extranjero, ficha nacional o sub 23) que el jugador que se va. La lista de jugadores disponibles para transferir solo mostrará aquellos que compartan la misma posición y categoría del jugador que estás vendiendo, para facilitar tu elección.
        </p>
        <p>
          <strong className="text-white">Transferencias pendientes:</strong> Al realizar una transferencia, esta quedará <strong className="text-white">pendiente</strong> hasta que presiones el botón "Guardar cambios". Podés hacer múltiples transferencias y verás todas las pendientes listadas en el modal. Desde ahí podés:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong className="text-white">Descartar individualmente:</strong> cada transferencia pendiente tiene un botón para eliminarla sin afectar las demás.
          </li>
          <li>
            <strong className="text-white">Guardar todas:</strong> al presionar "Guardar cambios", se aplicarán todas las transferencias pendientes de una vez.
          </li>
          <li>
            <strong className="text-white">Descartar todas:</strong> podés cancelar todas las transferencias pendientes con un solo clic.
          </li>
        </ul>
        <p>
          <strong className="text-white">Importante:</strong> Cuando realizás una transferencia , los jugadores nuevos ya se verán en tu equipo , pero <strong className="text-white">no quedará guardado</strong> hasta que presiones "Guardar cambios". Mientras tanto, podés modificar, agregar o eliminar transferencias sin límite. Si cerrás el modal o la página sin guardar, las transferencias pendientes se perderán. Una vez guardadas, se descontarán de tus 2 transferencias disponibles para la fecha.
        </p>
      </div>
    ),
  },
  {
    id: 'plazos',
    title: 'Plazos',
    content: (
      <div className="space-y-3 text-white/70 leading-relaxed">
        <p>
          Podés hacer sustituciones y transferencias hasta 15 minutos antes del primer partido de la fecha. Una vez que
          arranca la jornada, cualquier cambio queda guardado para la siguiente.
        </p>
        <p>
          En la página principal de tu equipo podés ver hasta cuándo podés hacer cambios y transferencias para la próxima jornada.
        </p>
      </div>
    ),
  },
  {
    id: 'puntajes',
    title: 'Puntajes',
    content: (
      <div className="space-y-5 text-white/70 leading-relaxed">
        <section className="space-y-4">
          <h3 className="text-base font-bold text-white">Valores por estadística</h3>
          <p>Cada estadística del boxscore suma o resta puntos fantasy con los siguientes valores:</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl p-3" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)' }}>
              <p className="font-bold text-white">Acciones positivas</p>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-sm">
                <li>Punto convertido: +1.0</li>
                <li>Rebote: +1.2</li>
                <li>Asistencia: +1.5</li>
                <li>Doble convertido: +0.5</li>
                <li>Triple convertido (extra): +1.0</li>
                <li>Tiro libre convertido: +0.3</li>
              </ul>
            </div>
            <div className="rounded-xl p-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
              <p className="font-bold text-white">Acciones negativas</p>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-sm">
                <li>Tiro de campo fallado: −0.3</li>
                <li>Tiro libre fallado: −0.2</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-bold text-white">Ejemplo de cálculo individual</h3>
          <p>Con los valores anteriores, así quedaría el cálculo para un jugador con las siguientes estadísticas:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>25 puntos</li>
            <li>8 rebotes</li>
            <li>6 asistencias</li>
            <li>10/18 en dobles (10 convertidos, 8 fallados)</li>
            <li>3/7 en triples (3 convertidos, 4 fallados)</li>
            <li>6/8 en libres (6 convertidos, 2 fallados)</li>
          </ul>
          <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex justify-between">
              <span>Puntos</span>
              <span className="text-white font-medium">25 × 1.0 = 25.0</span>
            </div>
            <div className="flex justify-between">
              <span>Rebotes</span>
              <span className="text-white font-medium">8 × 1.2 = 9.6</span>
            </div>
            <div className="flex justify-between">
              <span>Asistencias</span>
              <span className="text-white font-medium">6 × 1.5 = 9.0</span>
            </div>
            <div className="flex justify-between">
              <span>Dobles convertidos</span>
              <span className="text-white font-medium">10 × 0.5 = 5.0</span>
            </div>
            <div className="flex justify-between">
              <span>Triples convertidos</span>
              <span className="text-white font-medium">3 × 1.0 = 3.0</span>
            </div>
            <div className="flex justify-between">
              <span>Tiros libres convertidos</span>
              <span className="text-white font-medium">6 × 0.3 = 1.8</span>
            </div>
            <div className="flex justify-between">
              <span>Tiros de campo fallados</span>
              <span className="text-white font-medium">8 × -0.3 = -2.4</span>
            </div>
            <div className="flex justify-between">
              <span>Tiros libres fallados</span>
              <span className="text-white font-medium">2 × -0.2 = -0.4</span>
            </div>
            <div className="mt-3 border-t pt-3 flex justify-between font-bold text-white" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <span>Total Fantasy Points</span>
              <span style={{ color: '#4ade80' }}>50.6</span>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-bold text-white">Multiplicadores según el rol</h3>
          <p>
            Una vez calculados los puntos fantasy básicos de cada jugador, se aplica un multiplicador según el lugar que
            ocupa en tu equipo para esa fecha.
          </p>
          <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <table className="min-w-full text-sm">
              <thead style={{ background: 'rgba(255,255,255,0.05)' }}>
                <tr>
                  <th className="px-4 py-2 text-left font-bold text-white/50 uppercase tracking-wide text-[10px]">Rol</th>
                  <th className="px-4 py-2 text-left font-bold text-white/50 uppercase tracking-wide text-[10px]">
                    Multiplicador
                  </th>
                  <th className="px-4 py-2 text-left font-bold text-white/50 uppercase tracking-wide text-[10px]">
                    Ejemplo con 50.6 pts
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <tr className="divide-x-0">
                  <td className="px-4 py-2 text-white font-semibold">Capitán</td>
                  <td className="px-4 py-2" style={{ color: '#FFB800' }}>1.5×</td>
                  <td className="px-4 py-2">50.6 × 1.5 = 75.9 pts</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-white font-semibold">Titular</td>
                  <td className="px-4 py-2 text-blue-400">1.0×</td>
                  <td className="px-4 py-2">50.6 × 1.0 = 50.6 pts</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-white font-semibold">Suplente</td>
                  <td className="px-4 py-2 text-white/50">0.5×</td>
                  <td className="px-4 py-2">50.6 × 0.5 = 25.3 pts</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-base font-bold text-white">Redondeo de puntos</h3>
          <p>
            Los puntos fantasy se calculan con decimales, pero al mostrarse se redondean al número entero más cercano usando el método estándar de redondeo:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li>Si el decimal es menor a 0.5, se redondea hacia abajo (ej: 10.4 → 10 pts)</li>
            <li>Si el decimal es 0.5 o mayor, se redondea hacia arriba (ej: 10.5 → 11 pts, 10.6 → 11 pts)</li>
          </ul>
          <div className="rounded-lg p-3 text-sm" style={{ background: 'rgba(0,87,184,0.12)', border: '1px solid rgba(0,87,184,0.3)' }}>
            <p className="font-bold text-blue-300 mb-1">Ejemplo:</p>
            <p className="text-blue-200/80">
              Si un jugador obtiene <strong className="text-white">10.7</strong> puntos fantasy, se muestra como <strong className="text-white">11 pts</strong>.
            </p>
          </div>
        </section>
      </div>
    ),
  },
  {
    id: 'ligas',
    title: 'Ligas',
    content: (
      <div className="space-y-3 text-white/70 leading-relaxed">
        <p>
          Desde la pestaña Ligas podés ver todas las competiciones en las que participa tu equipo y crear nuevas. Tenés
          dos tipos disponibles:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-bold text-white">Ligas privadas:</span> necesitan una contraseña que definís
            al momento de crearlas. Solo quienes reciban la contraseña podrán sumarse.
          </li>
          <li>
            <span className="font-bold text-white">Ligas públicas:</span> cualquier usuario puede unirse sin
            restricciones. Ideales para competir con toda la comunidad.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: 'contacto',
    title: 'Contáctanos',
    content: (
      <div className="space-y-3 text-white/70 leading-relaxed">
        <p>
          ¿Tenés dudas, sugerencias o encontraste un problema? Escribinos a{' '}
          <a
            href="mailto:fantasylub2025@gmail.com"
            className="font-semibold underline"
            style={{ color: '#FFB800' }}
          >
            fantasylub2025@gmail.com
          </a>{' '}
          contándonos tu consulta. Si nos compartís tu usuario y el nombre de tu equipo, podemos ayudarte más rápido.
        </p>
      </div>
    ),
  },
];

const RulesPage: React.FC = () => {
  const [openSectionId, setOpenSectionId] = useState<string | null>('jornadas');

  const toggleSection = (sectionId: string) => {
    setOpenSectionId((prev) => (prev === sectionId ? null : sectionId));
  };

  return (
    <div className="min-h-screen">
      <div className="px-3 pt-2 pb-3 -mt-4 max-w-4xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl px-4 py-4 mb-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-0.5">FantasyLub</p>
          <h1 className="text-2xl font-black text-white">Reglamento</h1>
          <p className="mt-3 text-sm text-white/60 leading-relaxed">
            Fantasy LUB es el juego que te permite elegir y gestionar el equipo de tus sueños en la Liga Uruguaya de
            Basketball. Sumá puntos según el rendimiento real de los jugadores: armá tu plantel con inteligencia,
            aprovechá los cambios estratégicos y escalá posiciones en la clasificación fecha a fecha.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-2">
          {sections.map((section) => {
            const isOpen = section.id === openSectionId;
            return (
              <div
                key={section.id}
                className="rounded-2xl overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-white/5"
                >
                  <h2 className="text-sm font-bold text-white text-left">{section.title}</h2>
                  <span
                    className={`flex-shrink-0 transform transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : 'rotate-0'
                    }`}
                    style={{ color: '#FFB800' }}
                    aria-hidden="true"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-1 text-sm border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    {section.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RulesPage;
